import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router";
import { Menu, X, Search, ShoppingBag, Heart, Globe, User } from "lucide-react";
import { useCart } from "./CartContext";
import { useWishlist } from "./WishlistContext";
import { useLanguage, LANG_OPTIONS } from "./LanguageContext";
import { useAdminData } from "./admin/AdminDataContext";
import { SearchOverlay } from "./SearchOverlay";
import { useTheme } from "./ThemeInjector";
import { useCustomerAuth } from "./CustomerAuthContext";
import defaultLogoSvg from "../../imports/MoechaLogo.svg";

export function Navigation() {
  const { siteConfig } = useAdminData();
  const theme = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const { totalItems, setIsCartOpen } = useCart();
  const { wishlist } = useWishlist();
  const { lang, setLang, t } = useLanguage();
  const { isAuthenticated: customerLoggedIn, isAvailable: customerAuthAvailable } = useCustomerAuth();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const logo = siteConfig.logo || defaultLogoSvg;
  const wishlistEnabled = siteConfig.features?.wishlist !== false;
  const accountEnabled = siteConfig.features?.account === true;

  const menuItems = [
    { label: t("nav.shop"), to: "/#collection", section: "collection" },
    { label: t("nav.workshops"), to: "/#workshops", section: "workshops" },
    { label: t("nav.stories"), to: "/#journal", section: "journal" },
    { label: t("nav.wholesale"), to: "/wholesale" },
    { label: t("nav.about"), to: "/about" },
    { label: t("nav.faq"), to: "/#faq", section: "faq" },
  ];

  // Scroll to a hash target, with a small delay if navigating to home first
  const scrollToHash = (hash: string) => {
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      return true;
    }
    return false;
  };

  const handleNavClick = (item: typeof menuItems[number], e: React.MouseEvent) => {
    if (item.section && isHome) {
      // Already on homepage — just scroll, no navigation needed
      e.preventDefault();
      scrollToHash(item.section);
      setMenuOpen(false);
    } else if (item.section && !isHome) {
      // Going to homepage + section — navigate then scroll after render
      setMenuOpen(false);
      // Let the Link navigate; after the page renders, the hash will be in the URL
      // We use a timeout to allow the homepage to mount
      setTimeout(() => scrollToHash(item.section), 150);
    } else {
      setMenuOpen(false);
    }
  };

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Active section highlighting via IntersectionObserver (homepage only)
  const observeSections = useCallback(() => {
    if (!isHome) return;
    const sectionIds = ["collection", "workshops", "journal", "faq"];
    const observers: IntersectionObserver[] = [];

    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveSection(id);
          }
        },
        { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, [isHome]);

  useEffect(() => {
    const cleanup = observeSections();
    return cleanup;
  }, [observeSections]);

  useEffect(() => {
    if (!isHome) setActiveSection("");
  }, [isHome]);

  // Close language dropdown when clicking outside
  useEffect(() => {
    if (!langDropdownOpen) return;
    const handleClick = () => setLangDropdownOpen(false);
    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [langDropdownOpen]);

  const currentLangOption = LANG_OPTIONS.find((o) => o.code === lang) || LANG_OPTIONS[0];

  const renderLogo = (size: number, textColor: string, fontSize: string) => (
    <img src={logo} alt="MOECHA" className="h-10 object-contain" />
  );

  return (
    <>
      {/* Skip to content link */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:px-4 focus:py-2 focus:bg-[#6F7F63] focus:text-white focus:rounded-lg focus:outline-none"
        style={{ fontFamily: "'Jost', sans-serif", fontSize: "13px" }}
      >
        {t("skip_to_content")}
      </a>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-md shadow-[0_2px_16px_rgba(40,32,20,0.04)]`}
        style={{
          backgroundColor: scrolled ? theme.navScrolledBg : `${theme.navBg}e6`,
          borderBottom: `1px solid ${theme.border}80`,
          color: theme.navText,
        }}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between relative">
          {/* Left: Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-2 hover:opacity-70 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
            style={{ color: theme.navText }}
            aria-label={t("nav.menu")}
            aria-expanded={menuOpen}
          >
            {menuOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>

          {/* Center: Logo (absolutely centered) */}
          <Link to="/" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" aria-label="MOECHA - Home">
            <img src={logo} alt="MOECHA" className="h-10 object-contain" />
          </Link>

          {/* Right: Icons */}
          <div className="flex items-center gap-3">
            {/* Language dropdown */}
            <div className="relative hidden sm:block">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLangDropdownOpen(!langDropdownOpen);
                }}
                className="p-1 hover:opacity-70 transition-colors duration-200 flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
                style={{ color: theme.navText }}
                aria-label="Change language"
                aria-expanded={langDropdownOpen}
                aria-haspopup="listbox"
              >
                <Globe size={18} strokeWidth={1.5} />
                <span
                  className="tracking-[0.1em] uppercase"
                  style={{ fontFamily: "'Jost', sans-serif", fontSize: "10px" }}
                >
                  {currentLangOption.short}
                </span>
              </button>

              {langDropdownOpen && (
                <div
                  className="absolute right-0 top-full mt-2 bg-[#F7F5F1] border border-[#E6E0D7] rounded-lg shadow-[0_8px_30px_rgba(40,32,20,0.1)] py-1 min-w-[140px] z-50"
                  role="listbox"
                  aria-label="Select language"
                >
                  {LANG_OPTIONS.map((option) => (
                    <button
                      key={option.code}
                      onClick={(e) => {
                        e.stopPropagation();
                        setLang(option.code);
                        setLangDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2.5 flex items-center justify-between transition-colors duration-150 ${
                        lang === option.code
                          ? "text-[#6F7F63] bg-[#6F7F63]/5"
                          : "text-[#2D2A26] hover:bg-[#E9E4DC]/50"
                      }`}
                      style={{ fontFamily: "'Jost', sans-serif", fontSize: "13px" }}
                      role="option"
                      aria-selected={lang === option.code}
                    >
                      <span>{option.label}</span>
                      <span
                        className="tracking-[0.1em] uppercase opacity-40"
                        style={{ fontSize: "10px" }}
                      >
                        {option.short}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Search */}
            <button
              onClick={() => setSearchOpen(true)}
              className="p-1 hover:opacity-70 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
              style={{ color: theme.navText }}
              aria-label={t("nav.search")}
            >
              <Search size={20} strokeWidth={1.5} />
            </button>

            {/* Wishlist */}
            {wishlistEnabled && (
              <Link
                to="/"
                className="p-1 hover:opacity-70 transition-colors duration-200 relative hidden sm:block focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
                style={{ color: theme.navText }}
                aria-label="Wishlist"
              >
                <Heart size={20} strokeWidth={1.5} />
                {wishlist.length > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ fontSize: "10px", backgroundColor: theme.accent, color: theme.btnText }}
                  >
                    {wishlist.length}
                  </span>
                )}
              </Link>
            )}

            {/* Cart */}
            <button
              onClick={() => setIsCartOpen(true)}
              className="p-1 hover:opacity-70 transition-colors duration-200 relative focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
              style={{ color: theme.navText }}
              aria-label={`${t("nav.cart")} (${totalItems} items)`}
            >
              <ShoppingBag size={20} strokeWidth={1.5} />
              {totalItems > 0 && (
                <span
                  className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                  style={{ fontSize: "10px", backgroundColor: theme.accent, color: "#fff" }}
                >
                  {totalItems}
                </span>
              )}
            </button>

            {/* Customer Account (only when feature enabled in Site Config) */}
            {accountEnabled && customerAuthAvailable && (
              <Link
                to="/account"
                className="p-1 hover:opacity-70 transition-colors duration-200 relative focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg"
                style={{ color: theme.navText }}
                aria-label={t("nav.account")}
              >
                <User size={20} strokeWidth={1.5} />
                {customerLoggedIn && (
                  <span
                    className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2"
                    style={{ backgroundColor: theme.primary, borderColor: theme.navBg }}
                  />
                )}
              </Link>
            )}
          </div>
        </div>

        {/* Desktop nav links (visible when scrolled on homepage): Shop, Stories, Workshops, About, FAQ */}
        {isHome && scrolled && (
          <div className="hidden lg:flex justify-center gap-8 pb-3 px-6">
            {menuItems
              .filter((item) => item.section || item.to === "/about")
              .map((item) => {
                const href = item.section ? `#${item.section}` : item.to;
                const isActive = item.section ? activeSection === item.section : false;
                return (
                  <a
                    key={item.section || item.to}
                    href={href}
                    className={`tracking-[0.15em] uppercase transition-all duration-300 border-b-2 pb-0.5 ${
                      isActive ? "border-current" : "border-transparent hover:border-current/30"
                    }`}
                    style={{
                      fontFamily: theme.fontBody,
                      fontSize: "10px",
                      fontWeight: 400,
                      color: isActive ? theme.primary : theme.muted,
                    }}
                  >
                    {item.label}
                  </a>
                );
              })}
          </div>
        )}
      </nav>

      {/* Mobile Menu Overlay — scrollable when viewport is short */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-40 backdrop-blur-lg overflow-y-auto overscroll-contain py-20 px-6"
          style={{ backgroundColor: `${theme.bg}fa` }}
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
        >
          <div className="flex flex-col items-center gap-8 min-h-full justify-center">
            {menuItems.map((item) => (
              <Link
                key={item.label}
                to={item.to}
                className="tracking-[0.2em] uppercase focus:outline-none focus:ring-2 focus:ring-[#C7A76C] rounded-lg px-4 py-2 transition-colors"
                style={{ fontFamily: theme.fontHeading, fontSize: "24px", color: theme.fg }}
                onClick={(e) => handleNavClick(item, e)}
              >
                {item.label}
              </Link>
            ))}

            {/* Mobile language picker */}
            <div className="flex items-center gap-3 mt-4">
              <Globe size={18} strokeWidth={1.5} style={{ color: theme.muted }} />
              {LANG_OPTIONS.map((option) => (
                <button
                  key={option.code}
                  onClick={() => {
                    setLang(option.code);
                    setMenuOpen(false);
                  }}
                  className={`tracking-[0.15em] uppercase px-3 py-1.5 rounded-full transition-all duration-200 border ${
                    lang === option.code
                      ? "border-current/30"
                      : "border-transparent"
                  }`}
                  style={{
                    fontFamily: theme.fontBody,
                    fontSize: "11px",
                    color: lang === option.code ? theme.primary : theme.muted,
                    backgroundColor: lang === option.code ? `${theme.primary}1a` : "transparent",
                  }}
                >
                  {option.short}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search Overlay */}
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}